import { useEffect, useMemo, useRef, useState } from "react";
import "./OnlineUsers.scss";
import newRequest from "../../utils/newRequest";
import { CiImageOn } from "react-icons/ci";
import { useSocket } from "../../redux/useSocket";
import {
    FiDownload,
    FiMonitor,
    FiSmartphone,
    FiTablet,
    FiGlobe,
    FiClock,
    FiChevronLeft,
    FiChevronRight,
} from "react-icons/fi";
import { MdOutlineLaptopMac } from "react-icons/md";
import { HiOutlineStatusOnline } from "react-icons/hi";

const PAGE_SIZE = 6;

const normalizeUsers = (users = []) =>
    users.map((user) => ({
        ...user,
        usr_id: user?.usr_id || user?._id,
        usr_photo_url:
            user?.usr_photo_url ||
            user?.updated_by_photo_url_xx ||
            user?.entity_photo_url_xx ||
            "",
        usr_org_name: user?.usr_org_name || "Unknown Organization",
        usr_full_name: user?.usr_full_name || "Unknown User",
        usr_email: user?.usr_email || "",
        biz_unit_name_xx: user?.biz_unit_name_xx || "",
        biz_unit_code_xx: user?.biz_unit_code_xx || "",
        session_browser: user?.session_browser || "Unknown Browser",
        session_browser_version: user?.session_browser_version || "",
        session_platform: user?.session_platform || "Unknown Platform",
        time_ago_xx: user?.time_ago_xx || "Online now",
        user_type_xx: user?.user_type_xx || "user",
    }));

const getDeviceType = (platform = "") => {
    const p = String(platform).toLowerCase();

    if (p.includes("iphone") || p.includes("android") || p.includes("ios")) {
        return "Mobile";
    }
    if (p.includes("ipad") || p.includes("tablet")) {
        return "Tablet";
    }
    if (
        p.includes("mac") ||
        p.includes("win") ||
        p.includes("linux") ||
        p.includes("intel")
    ) {
        return "Desktop";
    }
    return "Other";
};

const getDeviceIcon = (platform = "") => {
    const type = getDeviceType(platform);

    if (type === "Mobile") return <FiSmartphone />;
    if (type === "Tablet") return <FiTablet />;
    if (type === "Desktop") return <MdOutlineLaptopMac />;
    return <FiMonitor />;
};

const getActivityScore = (user) => {
    const raw = Number(user?.session_browser_version || 0);
    if (!raw) return 72;
    return Math.min(99, 60 + raw);
};

const OnlineUsers = () => {
    const socket = useSocket();

    const [onlineUsers, setOnlineUsers] = useState([]);
    const [leavingUsers, setLeavingUsers] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState("All");
    const [page, setPage] = useState(1);

    const prevUsersRef = useRef([]);

    const fetchOnlineUsers = async () => {
        try {
            const res = await newRequest.get("/adUser/online");
            const nextUsers = normalizeUsers(res.data || []);
            const prevUsers = prevUsersRef.current || [];

            const prevIds = new Set(prevUsers.map((u) => u.usr_id));
            const nextIds = new Set(nextUsers.map((u) => u.usr_id));

            const removed = prevUsers.filter((u) => !nextIds.has(u.usr_id));

            if (removed.length) {
                setLeavingUsers((current) => {
                    const map = new Map(current.map((u) => [u.usr_id, u]));
                    removed.forEach((u) => map.set(u.usr_id, { ...u, isLeaving: true }));
                    return Array.from(map.values());
                });

                setTimeout(() => {
                    setLeavingUsers((current) =>
                        current.filter((u) => !removed.some((r) => r.usr_id === u.usr_id))
                    );
                }, 500);
            }

            const enrichedNext = nextUsers.map((u) => ({
                ...u,
                isEntering: !prevIds.has(u.usr_id),
            }));

            prevUsersRef.current = nextUsers;
            setOnlineUsers(enrichedNext);
        } catch (error) {
            console.error("Error fetching online users:", error);
        }
    };

    useEffect(() => {
        fetchOnlineUsers();

        if (!socket) return;

        const handler = async () => {
            await fetchOnlineUsers();
        };

        socket.on("refreshUsers", handler);

        return () => {
            socket.off("refreshUsers", handler);
        };
    }, [socket]);

    useEffect(() => {
        if (!onlineUsers.length) return;

        const timer = setTimeout(() => {
            setOnlineUsers((current) =>
                current.map((u) => ({ ...u, isEntering: false }))
            );
        }, 600);

        return () => clearTimeout(timer);
    }, [onlineUsers]);

    const mergedUsers = useMemo(() => {
        const liveIds = new Set(onlineUsers.map((u) => u.usr_id));
        const safeLeaving = leavingUsers.filter((u) => !liveIds.has(u.usr_id));
        return [...onlineUsers, ...safeLeaving];
    }, [onlineUsers, leavingUsers]);

    const organizations = useMemo(() => {
        const orgs = Array.from(
            new Set(mergedUsers.map((u) => u.usr_org_name).filter(Boolean))
        );
        return ["All", ...orgs];
    }, [mergedUsers]);

    const filteredUsers = useMemo(() => {
        if (selectedOrg === "All") return mergedUsers;
        return mergedUsers.filter((u) => u.usr_org_name === selectedOrg);
    }, [mergedUsers, selectedOrg]);

    const groupedUsers = useMemo(() => {
        const groups = {};

        filteredUsers.forEach((user) => {
            const org = user?.usr_org_name || "Unknown Organization";
            if (!groups[org]) groups[org] = [];
            groups[org].push(user);
        });

        return Object.entries(groups).map(([orgName, users]) => ({
            orgName,
            users,
        }));
    }, [filteredUsers]);

    const totalLive = onlineUsers.length;

    const deviceStats = useMemo(() => {
        const totals = {
            Desktop: 0,
            Mobile: 0,
            Tablet: 0,
            Other: 0,
        };

        onlineUsers.forEach((user) => {
            const type = getDeviceType(user?.session_platform);
            totals[type] += 1;
        });

        const total = onlineUsers.length || 1;

        return Object.entries(totals).map(([name, count]) => ({
            name,
            count,
            pct: ((count / total) * 100).toFixed(1),
        }));
    }, [onlineUsers]);

    const totalPages = Math.max(1, Math.ceil(groupedUsers.length / PAGE_SIZE));
    const pagedGroups = groupedUsers.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
    );

    useEffect(() => {
        setPage(1);
    }, [selectedOrg]);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    return (
        <div className="online-users-page">
            <div className="online-users">
                <div className="online-users-topbar">
                    <div className="online-users-title-block">
                        <h1 className="online-users-title">Online Users</h1>
                        <p className="online-users-subtitle">
                            Real-time analysis of users logged in.
                        </p>
                    </div>

                    <div className="online-users-topbar-actions">
                        <div className="live-pill">
                            <span className="live-dot" />
                            <span>Live Tracking</span>
                        </div>
                    </div>
                </div>

                <div className="online-users-dashboard">
                    <div className="online-users-main">


                        {/* <div className="online-users-feed-header">
                            <div className="feed-header-left">
                                <h2>Exploratory User Feed</h2>
                                <span className="feed-badge">Live Connections</span>
                            </div>
                        </div> */}

                        <div className="online-users-wrapper">
                            {pagedGroups.length === 0 ? (
                                <div className="online-users-empty">No users online.</div>
                            ) : (
                                pagedGroups.map((group) => (
                                    <div className="online-users-org-section" key={group.orgName}>
                                        <div className="online-users-org-title-row">
                                            <span className="org-line" />
                                            <h3>Organization: {group.orgName}</h3>
                                        </div>

                                        <div className="online-users-org-grid">
                                            {group.users.map((user) => (
                                                <div
                                                    className={[
                                                        "online-user-card",
                                                        user?.isEntering ? "entering" : "",
                                                        user?.isLeaving ? "leaving" : "",
                                                    ]
                                                        .join(" ")
                                                        .trim()}
                                                    key={`${user?.usr_id}-${user?.isLeaving ? "leaving" : "live"}`}
                                                >
                                                    <div className="online-user-card-top">
                                                        <div className="avatar-wrapper">
                                                            {user?.usr_photo_url ? (
                                                                <img
                                                                    src={user?.usr_photo_url}
                                                                    alt={user?.usr_full_name}
                                                                />
                                                            ) : (
                                                                <CiImageOn className="avatar-fallback" />
                                                            )}
                                                            <span className="status-dot" />
                                                        </div>

                                                        <div className="activity-score-block">
                                                            <span className="activity-score-label">
                                                                Activity Score
                                                            </span>
                                                            <span className="activity-score-value">
                                                                {getActivityScore(user)}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="online-user-main">
                                                        <div className="online-user-name">
                                                            {user?.usr_full_name}
                                                        </div>

                                                        <div className="online-user-email">
                                                            {user?.usr_email}
                                                        </div>

                                                        <div className="online-user-meta">
                                                            <div className="meta-item">
                                                                {getDeviceIcon(user?.session_platform)}
                                                                <span>{user?.session_platform}</span>
                                                            </div>

                                                            <div className="meta-item">
                                                                <FiGlobe />
                                                                <span>
                                                                    {user?.biz_unit_name_xx || "Unknown Location"}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <div className="online-user-submeta">
                                                            <span className="pill-soft">
                                                                {user?.session_browser}
                                                            </span>
                                                            <span className="pill-soft">
                                                                {user?.user_type_xx}
                                                            </span>
                                                            <span className="pill-soft">
                                                                {user?.biz_unit_code_xx}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="online-user-footer">
                                                        <div className="footer-item">
                                                            <FiClock />
                                                            <span>{user?.time_ago_xx}</span>
                                                        </div>

                                                        <div className="footer-state">
                                                            <HiOutlineStatusOnline />
                                                            <span>{user?.isLeaving ? "Leaving" : "Online"}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {totalPages > 1 && (
                            <div className="online-users-pagination">
                                <button
                                    type="button"
                                    className="page-arrow"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                >
                                    <FiChevronLeft />
                                </button>

                                {Array.from({ length: totalPages }).map((_, index) => {
                                    const num = index + 1;
                                    return (
                                        <button
                                            type="button"
                                            key={num}
                                            className={`page-number ${num === page ? "active" : ""}`}
                                            onClick={() => setPage(num)}
                                        >
                                            {num}
                                        </button>
                                    );
                                })}

                                <button
                                    type="button"
                                    className="page-arrow"
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                >
                                    <FiChevronRight />
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="online-users-side">
                        <div className="summary-card primary">
                            <span className="summary-label">Total Sessions</span>
                            <div className="summary-value">{totalLive.toLocaleString()}</div>
                            <div className="summary-trend">12% increase from last refresh</div>
                        </div>

                        {/* <div className="summary-card">
                            <div className="summary-mini-header">Avg. Session Duration</div>

                            <div className="avg-duration">4m 32s</div>

                            <div className="progress-track">
                                <div className="progress-bar" />
                            </div>

                            <div className="mini-user-preview">
                                {onlineUsers.slice(0, 1).map((user) => (
                                    <div className="mini-user-card" key={user?.usr_id}>
                                        <div className="mini-user-left">
                                            <div className="avatar-wrapper small">
                                                {user?.usr_photo_url ? (
                                                    <img
                                                        src={user?.usr_photo_url}
                                                        alt={user?.usr_full_name}
                                                    />
                                                ) : (
                                                    <CiImageOn className="avatar-fallback" />
                                                )}
                                                <span className="status-dot" />
                                            </div>

                                            <div className="mini-user-info">
                                                <div className="mini-user-name">
                                                    {user?.usr_full_name}
                                                </div>
                                                <div className="mini-user-email">{user?.usr_email}</div>
                                            </div>
                                        </div>

                                        <div className="mini-score-block">
                                            <span className="activity-score-label">
                                                Activity Score
                                            </span>
                                            <span className="activity-score-value">
                                                {getActivityScore(user)}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div> */}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OnlineUsers;